import styled from "styled-components";

import {
	Text,
	Button,
	ColorV2,
	InputPrice,
	Tabs,
} from "@adoptaunabuelo/react-components";
import ResumeStyled from "../../../components/container/ResumeContainer";
import { useEffect, useRef, useState } from "react";
import { State } from "../../../redux";
import { useSelector } from "react-redux";
import ReliefModal from "../../../components/modal/ReliefModal";
import media from "styled-media-query";

const Header = styled.div`
	padding: 24px 24px 16px;
	text-align: center;
	${media.lessThan("medium")`
		padding: 0;
		text-align: left;
    `}
`;
const DataView = styled.div<{ $isMobile?: boolean }>`
	display: flex;
	flex: 1;
	flex-direction: column;
	padding: ${(props) => (props.$isMobile ? "24px 24px 112px" : "24px")};
`;
const Row = styled.div`
	display: flex;
	flex-direction: row;
`;
const Col = styled.div`
	display: flex;
	flex-direction: column;
`;
const InputView = styled.div<{ $isMobile?: boolean }>`
	display: flex;
	flex-direction: column;
	flex: 1;
	justify-content: ${(props) => (props.$isMobile ? "flex-start" : "center")};
`;
const TabsView = styled.div<{ $isMobile?: boolean }>`
	margin-bottom: ${(props) => (props.$isMobile ? "40px" : "24px")};

	${(props) =>
		props.$isMobile
			? `
		@media (min-height: 700px) {
			margin-bottom: 96px;
		}

		@media (min-height: 820px) {
			margin-bottom: 132px;
		}
    `
			: ""}
`;
const PriceSelectorView = styled.div<{ $isMobile?: boolean }>`
	> div > div:first-child > * {
		max-width: 60% !important;
	}

	/* En navegadores de dispositivo real (WebKit/Chrome movil, no en el DevTools
	   de escritorio) los flex items con min-width:auto no encogen por debajo de su
	   ancho/contenido; las cajas de importe (width 247px) desbordan y el
	   contenedor de InputPrice (overflow-x:auto) muestra scroll horizontal.
	   min-width:0 permite que encojan y quepan. */
	> div > div:nth-child(2),
	> div > div:nth-child(2) > div {
		min-width: 0;
	}

	/* En esta vista InputPrice siempre muestra 3 importes que caben; nunca
	   queremos scroll horizontal ni auto-centrado de opciones.
	   - overflow-x:hidden (fallback, todos los navegadores): recorta el posible
	     desborde horizontal de la fila de importes.
	   - overflow:clip (iOS 16+/Chrome): ademas hace que el root de InputPrice NO
	     sea un contenedor scrollable, anulando el scrollTo de auto-centrado que
	     desplazaba el contenido al marcar el importe de la derecha en el movil.
	   El contenido cabe en vertical (scrollHeight == clientHeight), asi que
	   recortar tambien el eje Y no pierde nada. */
	> div {
		overflow-x: hidden;
		overflow: clip;
	}

	${(props) =>
		props.$isMobile
			? `
		> div > div:first-child > * {
			max-width: 60% !important;
		}

		> div {
			gap: 12px;
		}

		> div > div:nth-child(2) {
			gap: 10px;
		}

		> div > div:nth-child(2) > div,
		> div > div:nth-child(3) {
			border-radius: 24px;
			padding: 18px 16px;
		}

		/* El input "Otra cantidad" es un flex item en columna, asi que su
		   "height" no manda (lo hace el flex-basis) y queda mas alto que las
		   cajas de importe. Bajamos su padding vertical (18px -> 13px) para que
		   su alto (input 38px + 26px) sea 64px, igual que las cajas. */
		> div > div:nth-child(3) {
			padding-top: 13px;
			padding-bottom: 13px;
		}
    `
			: ""}
`;

const Resume = (props: Props) => {
	const appLocation = useSelector((state: State) => state.location.location);
	const isMobile = useSelector((state: State) => state.screen.isMobile);

	const [priceOptions, setPriceOptions] = useState<{ price: number }[]>([
		{ price: 9 },
		{ price: 15 },
		{ price: 20 },
	]);
	const [defaultPrice, setDefaultPrice] = useState<number>(0);
	const [conversion, setConversion] = useState<number>(1);
	const [selectedPrice, setSelectedPrice] = useState(15);
	const [selectedCharge, setSelectedCharge] = useState("monthly");
	const [reliefAmount, setReliefAmount] = useState("0");
	const [showReliefModal, setShowReliefModal] = useState(false);
	const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
	const inputViewRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
		};

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		const temp1 = getOptionsPrice(selectedCharge);
		setPriceOptions(temp1);
		const backendDefault = getDefaultPrice(selectedCharge);
		const middleOption = temp1[Math.floor(temp1.length / 2)];
		const temp2 = temp1.some((o) => o.price === backendDefault)
			? backendDefault
			: middleOption
				? middleOption.price
				: backendDefault;
		setDefaultPrice(temp2);
		setSelectedPrice(temp2);
		const temp3 = getConversionPrice();
		setConversion(temp3);
		props.onPriceChange &&
			props.onPriceChange(selectedPrice, selectedCharge);
	}, [selectedCharge, appLocation]);

	useEffect(() => {
		getReliefAmount(selectedPrice);
		props.onPriceChange &&
			props.onPriceChange(selectedPrice, selectedCharge);
	}, [selectedPrice, appLocation]);

	const getReliefAmount = (price: number) => {
		const reliefSection1 = price > 250 ? 250 : price;
		const reliefSection2 = price > 250 ? price - 250 : 0;
		const relief = reliefSection1 * 0.8 + reliefSection2 * 0.4;
		setReliefAmount(relief.toFixed(2));
	};

	const getDefaultPrice = (charge: string) => {
		if (charge === "monthly") {
			return appLocation
				? appLocation.currency.price.monthly.default
				: 15;
		} else if (charge === "annually") {
			return appLocation
				? appLocation.currency.price.annually.default
				: 150;
		} else {
			return appLocation
				? appLocation.currency.price.one.default
				: 15;
		}
	};

	const getOptionsPrice = (charge: string) => {
		if (charge === "monthly") {
			return appLocation &&
				appLocation.currency.price.monthly.inputOptions
				? appLocation.currency.price.monthly.inputOptions.map((i) => ({
					price: i,
				}))
				: [{ price: 9 }, { price: 15 }, { price: 20 }];
		} else if (charge === "annually") {
			return appLocation &&
				appLocation.currency.price.annually.inputOptions
				? appLocation.currency.price.annually.inputOptions.map((i) => ({
					price: i,
				}))
				: [{ price: 90 }, { price: 150 }, { price: 250 }];
		} else {
			return appLocation && appLocation.currency.price.one.inputOptions
				? appLocation.currency.price.one.inputOptions.map((i) => ({
					price: i,
				}))
				: [{ price: 9 }, { price: 15 }, { price: 20 }];
		}
	};

	const getConversionPrice = () => {
		return appLocation ? appLocation.currency.conversion : 1;
	};

	const reliefAmountParts = reliefAmount.split(".");

	const handleCustomPriceInput = (event: React.FormEvent<HTMLDivElement>) => {
		const target = event.target;
		if (!(target instanceof HTMLInputElement)) return;
		if (target.placeholder !== "Otra cantidad") return;

		//Solo importes enteros: parseInt descarta decimales que hayan podido
		//entrar (p. ej. pegando "9,3"), evitando importes como 9.3 que rompen
		//el subunit de Stripe (9.3 * 100 = 930.0000000000001).
		const price = parseInt(target.value, 10);
		if (!Number.isFinite(price) || price <= 0) {
			setSelectedPrice(0);
			return;
		}

		setSelectedPrice(price);
	};

	const handleCustomPriceFocus = (
		event: React.FocusEvent<HTMLDivElement>,
	) => {
		const target = event.target;
		if (!(target instanceof HTMLInputElement)) return;
		if (target.placeholder !== "Otra cantidad") return;

		//Al enfocar el importe libre, dejarlo sin valor hasta que el usuario
		//escriba: el botón mostrará solo "Quiero donar" (sin importe).
		setSelectedPrice(0);
	};

	const mobileResumeStyle: React.CSSProperties = isMobile
		? {
			minHeight:
				"calc(var(--doc-height) - var(--nav-bar-height-mobile))",
			borderRadius: "24px 24px 0 0",
			//El minWidth 380 de ResumeContainer es para la columna de
			//escritorio; en movil hace que la tarjeta no quepa dentro
			//del padding del contenedor y provoca scroll horizontal.
			minWidth: 0,
		}
		: {};
	const inputPriceKey = `${appLocation?.currency.name || "eur"}-${selectedCharge}-${priceOptions.map((option) => option.price).join("-")}-${defaultPrice}`;
	const tabsKey = `${isMobile ? "mobile" : "desktop"}-${windowWidth}`;

	//El input de importe libre es type="number" pero sin inputMode iOS abre el
	//teclado completo; forzamos el teclado numerico. Como el input lo pinta la
	//libreria (InputPrice) y se remonta con inputPriceKey, ajustamos el atributo
	//sobre el nodo montado cada vez que cambia.
	useEffect(() => {
		const input = inputViewRef.current?.querySelector<HTMLInputElement>(
			'input[placeholder="Otra cantidad"]',
		);
		if (input) {
			input.setAttribute("inputmode", "numeric");
			input.setAttribute("pattern", "[0-9]*");
		}
	}, [selectedCharge, inputPriceKey]);

	return (
		<ResumeStyled
			style={{
				padding: 0,
				overflow: "hidden",
				maxWidth: "100vw",
				marginBottom: 0,
				zIndex: 1,
				...mobileResumeStyle,
				...props.style,
			}}
			childrenProps={{
				style: { flex: 1 },
			}}
		>
			<ReliefModal
				isVisible={showReliefModal}
				price={selectedPrice}
				charge={selectedCharge}
				onClose={() => setShowReliefModal(false)}
			/>
			<Header>
				{!isMobile && (
					<Text
						type="h4"
						weight="semibold"
						style={{ textAlign: "center" }}
					>
						Apoya a las personas mayores siendo embajador
					</Text>
				)}
			</Header>

			<DataView $isMobile={isMobile}>
				{isMobile && (
					<Text
						type="h3"
						weight="semibold"
						style={{ marginBottom: 24, textAlign: "center" }}
					>
						Apoya a las personas mayores siendo embajador
					</Text>
				)}
				<InputView
					ref={inputViewRef}
					$isMobile={isMobile}
					onInput={handleCustomPriceInput}
					onFocus={handleCustomPriceFocus}
				>
					<TabsView $isMobile={isMobile}>
						<Tabs
							key={tabsKey}
							style={{ width: "100%" }}
							design={"third"}
							textColor={ColorV2.text.neutralHard}
							cellColor={ColorV2.surface.neutralSoft}
							options={[
								{
									id: "monthly",
									title: "Mensual",
								},
								{
									id: "annually",
									title: "Anual",
								},
								{
									id: "unique",
									title: "Puntual",
								},
							]}
							onChange={(op) => {
								setSelectedCharge(op.id);
							}}
						/>
					</TabsView>
					<PriceSelectorView $isMobile={isMobile}>
						<InputPrice
							key={inputPriceKey}
							style={{
								marginBottom:
									selectedCharge === "unique" ? 16 : 32,
							}}
							currency={
								appLocation ? appLocation.currency.symbol : "€"
							}
							options={priceOptions}
							defaultOption={defaultPrice}
							label={
								isMobile
									? "Acompañaremos a un mayor durante {{value}} horas"
									: "Acompañaremos a un mayor durante {{value}} horas"
							}
							labelValueConversion={0.8 * conversion}
							hideCustomAmount={selectedCharge !== "unique"}
							onChange={(price) => setSelectedPrice(price)}
							onErrorChange={(hasError) =>
								props.onErrorChange &&
								props.onErrorChange(hasError)
							}
						/>
					</PriceSelectorView>
				</InputView>
				{appLocation &&
					appLocation.country_code === "ES" &&
					appLocation.currency.name === "eur" && (
						<div style={{ marginBottom: isMobile ? 0 : 24 }}>
							<Text
								type="c1"
								style={{
									textAlign: "center",
								}}
							>
								Te devolverán{" "}
								<span
									style={{
										backgroundColor:
											ColorV2.surface.secondary,
										padding: "1px 6px",
										borderRadius: 3,
										fontWeight: 500,
									}}
								>
									{reliefAmountParts[0]}
									<span>
										{reliefAmountParts[1] !== "00" &&
											"," + reliefAmountParts[1]}
										{" €"}
									</span>
								</span>{" "}
								en la Declaración de la Renta.{" "}
								<span
									style={{
										textDecoration: "underline",
										cursor: "pointer",
									}}
									onClick={() => setShowReliefModal(true)}
								>
									Saber más
								</span>
							</Text>
						</div>
					)}
				<Col
					style={{
						gap: 8,
						display: isMobile ? "none" : "flex",
					}}
				>
					<Button
						loading={props.loading}
						onClick={props.onClick}
					>
						{selectedPrice
							? `Quiero donar ${selectedPrice.toLocaleString(
								"es-ES",
								{ useGrouping: true },
							)} ${appLocation ? appLocation.currency.symbol : "€"}${selectedCharge === "monthly"
								? " al mes"
								: selectedCharge === "annually"
									? " al año"
									: ""
							}`
							: "Quiero donar"}
					</Button>
				</Col>
			</DataView>
		</ResumeStyled>
	);
};
export default Resume;
export interface Props {
	style?: React.CSSProperties;
	loading: boolean;
	onClick: () => void;
	onPriceChange?: (price: number, charge: string) => void;
	onErrorChange?: (hasError: boolean) => void;
	onShareClick: (op: { id: string }) => void;
}
